/** Ping-pong delay: DelayNode pair, feedback gains, lowpass tone, stereo merge. */
import type { Drone } from '@tonist/shared';
import { DELAY_FEEDBACK_MAX, LOOP_LOWPASS_Q_DB, clamp, glideTo, softClipCurve, targetTau } from '../params';

type DelaySpec = Drone['space']['delay'];

export class PingPongDelay {
  readonly input: GainNode;
  readonly output: GainNode;
  private readonly dL: DelayNode;
  private readonly dR: DelayNode;
  private readonly toneL: BiquadFilterNode;
  private readonly toneR: BiquadFilterNode;
  private readonly fbL: GainNode;
  private readonly fbR: GainNode;
  private readonly clipL: WaveShaperNode;
  private readonly clipR: WaveShaperNode;
  private readonly merger: ChannelMergerNode;
  private pingPong: boolean | null = null;

  constructor(ctx: BaseAudioContext, spec: DelaySpec) {
    this.input = ctx.createGain();
    this.output = ctx.createGain();
    this.dL = ctx.createDelay(3);
    this.dR = ctx.createDelay(3);
    this.toneL = ctx.createBiquadFilter();
    this.toneR = ctx.createBiquadFilter();
    this.toneL.type = this.toneR.type = 'lowpass';
    // peak-free tone filters and a soft clipper in each loop: the recirculation can never exceed unity gain
    this.toneL.Q.value = this.toneR.Q.value = LOOP_LOWPASS_Q_DB;
    this.clipL = ctx.createWaveShaper(); this.clipL.curve = softClipCurve();
    this.clipR = ctx.createWaveShaper(); this.clipR.curve = softClipCurve();
    this.fbL = ctx.createGain();
    this.fbR = ctx.createGain();
    this.merger = ctx.createChannelMerger(2);
    this.dL.delayTime.value = spec.timeSec;
    this.dR.delayTime.value = spec.timeSec;
    this.toneL.frequency.value = this.toneR.frequency.value = spec.toneHz;
    this.fbL.gain.value = this.fbR.gain.value = clamp(spec.feedback, 0, DELAY_FEEDBACK_MAX);
    this.input.connect(this.dL);
    this.dL.connect(this.toneL); this.toneL.connect(this.clipL); this.clipL.connect(this.fbL);
    this.dR.connect(this.toneR); this.toneR.connect(this.clipR); this.clipR.connect(this.fbR);
    this.dL.connect(this.merger, 0, 0);
    this.dR.connect(this.merger, 0, 1);
    this.merger.connect(this.output);
    this.route(spec.pingPong);
  }

  private route(pingPong: boolean): void {
    if (this.pingPong === pingPong) return;
    this.pingPong = pingPong;
    try { this.fbL.disconnect(); this.fbR.disconnect(); } catch { /* first time */ }
    try { this.input.disconnect(this.dR); } catch { /* not connected */ }
    if (pingPong) {
      this.fbL.connect(this.dR);
      this.fbR.connect(this.dL);
    } else {
      this.fbL.connect(this.dL);
      this.fbR.connect(this.dR);
      this.input.connect(this.dR);
    }
  }

  set(spec: DelaySpec, t: number): void {
    this.route(spec.pingPong);
    glideTo(this.dL.delayTime, spec.timeSec, t, 0.3);
    glideTo(this.dR.delayTime, spec.timeSec, t, 0.3);
    targetTau(this.fbL.gain, clamp(spec.feedback, 0, DELAY_FEEDBACK_MAX), t, 0.1);
    targetTau(this.fbR.gain, clamp(spec.feedback, 0, DELAY_FEEDBACK_MAX), t, 0.1);
    targetTau(this.toneL.frequency, spec.toneHz, t, 0.1);
    targetTau(this.toneR.frequency, spec.toneHz, t, 0.1);
  }

  dispose(): void {
    for (const n of [this.input, this.output, this.dL, this.dR, this.toneL, this.toneR, this.clipL, this.clipR, this.fbL, this.fbR, this.merger]) {
      try { n.disconnect(); } catch { /* gone */ }
    }
  }
}
